import { useState } from 'react';
import type { DokumentyDokumentDto } from '@gov-nx/api';
import { useDownloadEvents } from '@gov-nx/core/events';
import { hasProperty, is } from '@gov-nx/core/types';

type Variables = { documentId: number; souborId?: string };

const useVariablesArray = () => {
	const [state, setState] = useState<Variables[]>([]);

	return {
		set: setState,
		clear: () => setState([]),
		isInArray: (documentId: number) =>
			state.some((item) => item.documentId === documentId),
		isEmpty: state.length === 0,
	};
};

interface MyFilesDownloadProps {
	onSelectedDocumentsSuccess?: () => void;
	onDocumentSuccess?: () => void;
}

export const useMyFilesDownload = (props?: MyFilesDownloadProps) => {
	const downloadEvents = useDownloadEvents();
	const downloadingDocumentIds = useVariablesArray();

	const downloadSelectedDocuments = async (variablesList: Variables[]) => {
		const variables = variablesList.filter(hasProperty('souborId'));
		downloadingDocumentIds.set(variables);
		await Promise.all(
			variables.map(
				(variables) =>
					new Promise((resolve) =>
						downloadEvents.downloadDocument({
							documentId: variables.documentId,
							souborId: variables.souborId,
							callback: resolve,
						})
					)
			)
		);
		props?.onSelectedDocumentsSuccess && props.onSelectedDocumentsSuccess();
		downloadingDocumentIds.clear();
	};

	const downloadDocument = ({
		documentId,
		souborId,
	}: {
		documentId: number;
		souborId: string;
	}) => {
		downloadingDocumentIds.set([{ documentId, souborId }]);
		downloadEvents.downloadDocument({
			documentId,
			souborId,
			callback: () => {
				downloadingDocumentIds.clear();
				props?.onDocumentSuccess && props.onDocumentSuccess();
			},
		});
	};

	const hasFileToDownload = (document: DokumentyDokumentDto): boolean => {
		const { soubory } = document;
		const { velikost, pripona } = soubory?.[0] ?? {};
		return (is(velikost) && velikost > 0) || is(pripona);
	};

	return {
		downloadDocument,
		downloadSelectedDocuments,
		hasFileToDownload,
		isDownloading: downloadingDocumentIds.isInArray,
		isAnyDocumentDownloading: !downloadingDocumentIds.isEmpty,
	};
};
